import { api } from "../../../lib/api";
import {
  VendorRegistration,
  VendorRegistrationsResponse,
  VendorStatsResponse,
  VendorDetailResponse,
  UpdateVendorStatusPayload,
  VendorQueryParams,
} from "./vendor.types";

class VendorService {
  /**
   * Get all vendor registrations with filters
   * @param {VendorQueryParams} params
   * @returns {Promise<VendorRegistrationsResponse>}
   */
  async getRegistrations(params) {
    try {
      const response = await api.get("contact/admin/vendor", { params });

      if (response.data.status && response.data.data) {
        const { registrations, pagination } = response.data.data;

        const vendorRegistrations = registrations.map(
          (reg) =>
            new VendorRegistration(
              reg.id,
              reg.name,
              reg.phone_number,
              reg.company_store_name,
              reg.message,
              reg.status,
              reg.created_at,
              reg.updated_at
            )
        );

        return new VendorRegistrationsResponse(
          true,
          vendorRegistrations,
          pagination
        );
      }

      return new VendorRegistrationsResponse(false, [], null);
    } catch (error) {
      const message =
        error.response?.data?.data?.error ||
        "Failed to fetch vendor registrations";
      throw new Error(message);
    }
  }

  /**
   * Get single vendor registration
   * @param {number} id
   * @returns {Promise<VendorDetailResponse>}
   */
  async getRegistration(id) {
    try {
      const response = await api.get(`contact/admin/vendor/${id}`);

      if (response.data.status && response.data.data) {
        const reg = response.data.data.registration;

        const vendorRegistration = new VendorRegistration(
          reg.id,
          reg.name,
          reg.phone_number,
          reg.company_store_name,
          reg.message,
          reg.status,
          reg.created_at,
          reg.updated_at
        );

        return new VendorDetailResponse(true, vendorRegistration);
      }

      return new VendorDetailResponse(false, null);
    } catch (error) {
      const message =
        error.response?.data?.data?.error ||
        "Failed to fetch vendor registration";
      throw new Error(message);
    }
  }

  /**
   * Get vendor statistics
   * @returns {Promise<VendorStatsResponse>}
   */
  async getStats() {
    try {
      const response = await api.get("contact/admin/vendor-stats");

      if (response.data.status && response.data.data) {
        return new VendorStatsResponse(true, response.data.data.stats);
      }

      return new VendorStatsResponse(false, null);
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to fetch statistics";
      throw new Error(message);
    }
  }

  /**
   * Update vendor registration status
   * @param {number} id
   * @param {UpdateVendorStatusPayload} payload
   * @returns {Promise<{status: boolean, message: string, data: any}>}
   */
  async updateStatus(id, payload) {
    try {
      const response = await api.patch(`contact/admin/vendor/${id}`, payload);

      return {
        status: response.data.status,
        message: response.data.data.message,
        data: response.data.data.registration,
      };
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to update status";
      throw new Error(message);
    }
  }

  /**
   * Delete a vendor registration
   * @param {number} id
   * @returns {Promise<{status: boolean, message: string}>}
   */
  async deleteRegistration(id) {
    try {
      const response = await api.delete(`contact/admin/vendor/${id}`);

      return {
        status: response.data.status,
        message: response.data.data.message,
      };
    } catch (error) {
      const message =
        error.response?.data?.data?.error || "Failed to delete registration";
      throw new Error(message);
    }
  }
}

export const vendorService = new VendorService();